import React, { useContext } from "react";

import { IntlContext } from "context";
import { getCategoryVisual } from "constants/categories";
import type { CatalogService } from "types/service";
import { SUBCATEGORY_TO_CATEGORY, type SubCategory } from "types/subCategory";
import { Badge } from "components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "components/ui/dialog";

interface Props {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    services: CatalogService[];
    firstName: string;
    onSelect: (serviceId: string) => void;
}

function servicePresentation(service: CatalogService, formatMessage: (id: string) => string) {
    const categoryFromSubcategory =
        service.subcategory && SUBCATEGORY_TO_CATEGORY[service.subcategory as SubCategory]?.toLowerCase();
    const categoryKey = categoryFromSubcategory ?? String(service.category ?? "").toLowerCase();
    const { palette, Icon } = getCategoryVisual(categoryKey);
    const label = service.subcategory
        ? formatMessage(`our_services.label.${service.subcategory}`)
        : formatMessage(`category.${categoryKey}`);

    return { palette, Icon, label };
}

export const ProviderContactServiceDialog: React.FC<Props> = ({
    open,
    onOpenChange,
    services,
    firstName,
    onSelect,
}) => {
    const intl = useContext(IntlContext);
    const formatMessage = (id: string) => String(intl.formatMessage(id));

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-md gap-5 sm:rounded-2xl">
                <DialogHeader className="space-y-2 pr-6 text-left">
                    <DialogTitle className="text-base leading-snug">
                        {intl.formatMessage("provider.card.contact_service.title", { firstName })}
                    </DialogTitle>
                    <DialogDescription>
                        {intl.formatMessage("provider.card.contact_service.description")}
                    </DialogDescription>
                </DialogHeader>

                <ul className="max-h-[60vh] space-y-2 overflow-y-auto">
                    {services.map((service) => {
                        const { palette, Icon, label } = servicePresentation(service, formatMessage);
                        const priceLabel = `${Number(service.price).toFixed(2)}€${formatMessage("provider.card.price_per_hour")}`;

                        return (
                            <li key={service.id}>
                                <button
                                    type="button"
                                    className="flex w-full items-center gap-3 rounded-xl bg-muted/40 p-3 text-left transition-colors hover:bg-muted/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                                    onClick={() => onSelect(service.id)}
                                >
                                    <Badge variant="iconTile" palette={palette} size="tileMd">
                                        <Icon className="h-4 w-4" />
                                    </Badge>
                                    <span className="min-w-0 flex-1 text-sm font-semibold text-foreground">{label}</span>
                                    <span className="shrink-0 text-sm font-extrabold text-foreground">{priceLabel}</span>
                                </button>
                            </li>
                        );
                    })}
                </ul>
            </DialogContent>
        </Dialog>
    );
};
